/* ============================================================
   Wishlist – localStorage + Share-Link (Interim)
   v2: Handle-basierter Fetch, korrekter Preisformat, X-Button Fix
   ============================================================ */

const Wishlist = (() => {
  const STORAGE_KEY = 'wl_items';

  // ── Storage ──────────────────────────────────────────────
  // Speichert [{id: '123', handle: 'ring-gold'}]

  function getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function saveItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  function addItem(id, handle) {
    const items = getItems();
    if (!items.find(i => String(i.id) === String(id))) {
      items.push({ id: String(id), handle });
      saveItems(items);
    }
  }

  function removeItem(id) {
    saveItems(getItems().filter(i => String(i.id) !== String(id)));
  }

  function hasItem(id) {
    return getItems().some(i => String(i.id) === String(id));
  }

  function clear() {
    localStorage.removeItem(STORAGE_KEY);
  }

  // ── Buttons ───────────────────────────────────────────────

  function syncButtons() {
    document.querySelectorAll('.wl-btn').forEach(btn => {
      const id = String(btn.dataset.productId);
      const active = hasItem(id);
      btn.classList.toggle('wl-btn--active', active);
      btn.setAttribute('aria-label', active ? 'Von Merkliste entfernen' : 'Zur Merkliste hinzufügen');
    });

    const count = getItems().length;
    document.querySelectorAll('.wl-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  // ── Toggle ────────────────────────────────────────────────

  function toggle(id, handle) {
    if (hasItem(id)) {
      removeItem(id);
    } else {
      addItem(id, handle);
      maybeShowNudge();
    }
    syncButtons();
    renderDrawer();
  }

  // ── Nudge ─────────────────────────────────────────────────

  function maybeShowNudge() {
    if (sessionStorage.getItem('wl_nudge_shown')) return;
    if (getItems().length < 3) return;
    const nudge = document.getElementById('wl-nudge');
    if (nudge) {
      nudge.classList.add('wl-nudge--visible');
      sessionStorage.setItem('wl_nudge_shown', '1');
    }
  }

  function dismissNudge() {
    const nudge = document.getElementById('wl-nudge');
    if (nudge) nudge.classList.remove('wl-nudge--visible');
  }

  // ── Drawer ────────────────────────────────────────────────

  function openDrawer() {
    renderDrawer();
    document.getElementById('wl-drawer')?.classList.add('wl-drawer--open');
    document.getElementById('wl-overlay')?.classList.add('wl-overlay--visible');
    document.body.classList.add('wl-no-scroll');
  }

  function closeDrawer() {
    document.getElementById('wl-drawer')?.classList.remove('wl-drawer--open');
    document.getElementById('wl-overlay')?.classList.remove('wl-overlay--visible');
    document.body.classList.remove('wl-no-scroll');
  }

  async function renderDrawer() {
    const items = getItems();
    const container = document.getElementById('wl-drawer-items');
    if (!container) return;

    if (items.length === 0) {
      container.innerHTML = '<p class="wl-empty">Deine Merkliste ist leer.</p>';
      updateShareButton(false);
      return;
    }

    container.innerHTML = '<p class="wl-loading">Wird geladen…</p>';

    try {
      const products = await fetchProducts(items);
      container.innerHTML = '';
      products.forEach(p => container.appendChild(buildCard(p)));
      updateShareButton(true);
    } catch {
      container.innerHTML = '<p class="wl-error">Fehler beim Laden. Bitte Seite neu laden.</p>';
    }
  }

  // Handle-basierter Fetch – zuverlässig
  async function fetchProducts(items) {
    const results = await Promise.all(
      items.map(item =>
        fetch(`/products/${item.handle}.json`)
          .then(r => r.json())
          .then(d => d.product || null)
          .catch(() => null)
      )
    );
    return results.filter(Boolean);
  }

  function buildCard(product) {
    const variant = product.variants?.[0];
    const price = variant ? formatMoney(parseFloat(variant.price)) : '';
    const image = product.images?.[0]?.src || '';
    const url = `/products/${product.handle}`;

    const el = document.createElement('div');
    el.className = 'wl-card';
    el.dataset.productId = String(product.id);
    el.innerHTML = `
      <a href="${url}" class="wl-card__image-link">
        ${image ? `<img src="${image}" alt="${product.title}" loading="lazy">` : '<div class="wl-card__no-image"></div>'}
      </a>
      <div class="wl-card__info">
        <a href="${url}" class="wl-card__title">${product.title}</a>
        ${price ? `<span class="wl-card__price">${price}</span>` : ''}
      </div>
      <button class="wl-card__remove" data-remove-id="${product.id}" aria-label="Entfernen" type="button">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    // Direkt gebunden, nicht per Delegation – vermeidet Timing-Probleme
    el.querySelector('.wl-card__remove').addEventListener('click', () => {
      removeItem(String(product.id));
      syncButtons();
      renderDrawer();
    });

    return el;
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  }

  function updateShareButton(hasItems) {
    const btn = document.getElementById('wl-share-btn');
    if (btn) btn.disabled = !hasItems;
  }

  // ── Share-Link ────────────────────────────────────────────

  function getShareUrl() {
    const handles = getItems().map(i => i.handle).join(',');
    return `${window.location.origin}/pages/merkliste?items=${handles}`;
  }

  function openShareMenu() {
    const url = getShareUrl();
    const menu = document.getElementById('wl-share-menu');
    if (!menu) return;

    // URLs dynamisch setzen
    menu.querySelector('#wl-share-whatsapp').href =
      `https://wa.me/?text=${encodeURIComponent('Schau dir diese Stücke an: ' + url)}`;
    menu.querySelector('#wl-share-email').href =
      `mailto:?subject=${encodeURIComponent('Meine Merkliste')}&body=${encodeURIComponent('Ich habe ein paar Stücke für dich gemerkt:\n\n' + url)}`;

    menu.classList.toggle('wl-share-menu--open');
  }

  function copyShareLink() {
    const url = getShareUrl();
    navigator.clipboard.writeText(url).then(() => {
      const btn = document.getElementById('wl-copy-link-btn');
      if (btn) {
        const original = btn.textContent;
        btn.textContent = '✓ Link kopiert';
        setTimeout(() => (btn.textContent = original), 2500);
      }
    });
  }

  // ── Event-Delegation ──────────────────────────────────────

  function bindEvents() {
    document.addEventListener('click', e => {
      const wlBtn = e.target.closest('.wl-btn');
      if (wlBtn) {
        e.preventDefault();
        const id = wlBtn.dataset.productId;
        const handle = wlBtn.dataset.productHandle;
        toggle(id, handle);
        return;
      }

      if (e.target.closest('#wl-header-icon')) {
        openDrawer();
        return;
      }

      if (e.target.closest('#wl-drawer-close') || e.target.closest('#wl-overlay')) {
        closeDrawer();
        return;
      }

      if (e.target.closest('#wl-share-btn')) {
        openShareMenu();
        return;
      }

      if (e.target.closest('#wl-copy-link-btn')) {
        copyShareLink();
        return;
      }

      if (e.target.closest('#wl-nudge-dismiss')) {
        dismissNudge();
        return;
      }
    });
  }

  // ── Init ──────────────────────────────────────────────────

  function init() {
    bindEvents();
    syncButtons();

    if (window.location.pathname.includes('/pages/merkliste')) {
      initSharePage();
    }
  }

  // ── Share-Page ────────────────────────────────────────────

  async function initSharePage() {
    const params = new URLSearchParams(window.location.search);
    const handles = params.get('items')?.split(',').filter(Boolean) || [];
    const container = document.getElementById('wl-share-page-items');
    if (!container || handles.length === 0) return;

    container.innerHTML = '<p class="wl-loading">Wird geladen…</p>';

    try {
      const products = await Promise.all(
        handles.map(handle =>
          fetch(`/products/${handle}.json`)
            .then(r => r.json())
            .then(d => d.product || null)
            .catch(() => null)
        )
      );

      container.innerHTML = '';
      products.filter(Boolean).forEach(p => {
        const variant = p.variants?.[0];
        const price = variant ? formatMoney(parseFloat(variant.price)) : '';
        const image = p.images?.[0]?.src || '';
        const url = `/products/${p.handle}`;

        const card = document.createElement('div');
        card.className = 'wl-share-card';
        card.innerHTML = `
          <a href="${url}">
            ${image ? `<img src="${image}" alt="${p.title}" loading="lazy">` : ''}
          </a>
          <div class="wl-share-card__info">
            <a href="${url}" class="wl-share-card__title">${p.title}</a>
            ${price ? `<span class="wl-share-card__price">${price}</span>` : ''}
            <a href="${url}" class="wl-share-card__link">Zum Stück →</a>
          </div>
        `;
        container.appendChild(card);
      });
    } catch {
      container.innerHTML = '<p class="wl-error">Fehler beim Laden.</p>';
    }
  }

  return { init, toggle, openDrawer, closeDrawer, syncButtons, getShareUrl, copyShareLink, clear };
})();

document.addEventListener('DOMContentLoaded', Wishlist.init);